import { useState } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'square';
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

const SIZES: Record<AvatarSize, string> = {
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
  xl: 'size-16',
};

const INITIALS_TEXT: Record<AvatarSize, string> = {
  // `text-2xs` (11px) is the smallest step on the type scale; `text-[9px]` was
  // a tenth size that existed in this file and nowhere else.
  xs: 'text-2xs',
  sm: 'text-2xs',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-lg',
};

const STATUS_DOT: Record<AvatarSize, string> = {
  xs: 'size-1.5',
  sm: 'size-2',
  md: 'size-2.5',
  lg: 'size-3',
  xl: 'size-3.5',
};

const STATUS_LABEL: Record<AvatarStatus, string> = {
  online: 'online',
  offline: 'offline',
  busy: 'busy',
  away: 'away',
};

/** Status is never carried by colour alone — the text equivalent rides along. */
const STATUS_COLOR: Record<AvatarStatus, string> = {
  online: 'bg-success',
  offline: 'bg-neutral',
  busy: 'bg-danger',
  away: 'bg-warning',
};

/** First letter of the first and last word: "Ada Lovelace" → "AL". */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return letters || '?';
}

export interface AvatarProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /** Falls back to initials when this is absent or fails to load. */
  src?: string;
  /** Used for the accessible label and for the initials fallback. */
  name: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  status?: AvatarStatus;
  /** Replaces the derived initials. */
  fallback?: ReactNode;
}

/**
 * The wrapper carries `role="img"` and the name, and everything inside it is
 * `aria-hidden`. That way the screen reader hears one named image whether the
 * `<img>` renders or the initials fallback does — the two are never announced
 * as different things.
 */
export function Avatar({
  src,
  name,
  size = 'md',
  shape = 'circle',
  status,
  fallback,
  className,
  ...rest
}: AvatarProps) {
  // Tracked by value rather than boolean so a new `src` retries automatically.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && failedSrc !== src;

  return (
    <span
      {...rest}
      role="img"
      aria-label={status ? `${name} — ${STATUS_LABEL[status]}` : name}
      className={cn('relative inline-grid shrink-0 place-items-center', SIZES[size], className)}
    >
      <span
        aria-hidden="true"
        className={cn(
          'relative grid size-full place-items-center overflow-hidden border border-line-subtle',
          'bg-primary-subtle font-display font-semibold uppercase text-heading select-none',
          shape === 'circle' ? 'rounded-pill' : 'rounded-xs',
        )}
      >
        {/* The initials render first and stay underneath, so the mark is never
            an empty box while the image is still decoding. */}
        <span className={INITIALS_TEXT[size]}>{fallback ?? initialsOf(name)}</span>
        {showImage ? (
          <img
            src={src}
            alt=""
            decoding="async"
            onError={() => setFailedSrc(src ?? null)}
            className="absolute inset-0 size-full object-cover"
          />
        ) : null}
      </span>

      {status ? (
        <span
          aria-hidden="true"
          className={cn(
            'absolute right-0 bottom-0 rounded-pill border-2 border-surface',
            STATUS_DOT[size],
            STATUS_COLOR[status],
          )}
        />
      ) : null}
    </span>
  );
}
